/**
 * Frame20 - Death Certificate Overview
 *
 * Purpose: Introduction to obtaining death certificates and why they're needed
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { SettlementLayout } from '../settlement/SettlementLayout';

export default function Frame20() {
  const navigate = useNavigate();
  const { estateId } = useParams();

  const handleNext = () => {
    navigate(estateId ? `/frame-20-1/${estateId}` : '/frame-20-1');
  };

  return (
    <SettlementLayout
      currentPhase="iad"
      currentStep="death-certificates"
      estateId={estateId || null}
    >
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Obtain Death Certificates</h1>
          <p className="text-gray-600">
            Certified death certificates are required for nearly every step of estate settlement
          </p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-yellow-900 mb-2">Important: Get Multiple Copies</h3>
              <p className="text-yellow-800">
                Most estates require 10-15 certified copies. Each institution (banks, insurance
                companies, government agencies) typically requires an original certified copy.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Why Death Certificates Are Needed
          </h2>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-cyan-100 text-cyan-600 rounded-full flex items-center justify-center font-semibold">
                1
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Financial Institutions</h4>
                <p className="text-gray-600">
                  Banks, investment firms, and insurance companies require death certificates to
                  freeze accounts, transfer assets, and process claims.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-cyan-100 text-cyan-600 rounded-full flex items-center justify-center font-semibold">
                2
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Government Agencies</h4>
                <p className="text-gray-600">
                  Social Security, VA, Medicare, and state agencies require certificates to stop
                  benefits and process final payments.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-cyan-100 text-cyan-600 rounded-full flex items-center justify-center font-semibold">
                3
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Legal Proceedings</h4>
                <p className="text-gray-600">
                  Probate court, title transfers, and legal filings all require certified death
                  certificates.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-cyan-100 text-cyan-600 rounded-full flex items-center justify-center font-semibold">
                4
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Service Providers</h4>
                <p className="text-gray-600">
                  Utilities, subscriptions, and creditors may require certificates to close or
                  transfer accounts.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">How to Obtain Certificates</h2>
          <div className="space-y-3">
            <div className="border-l-4 border-cyan-600 pl-4">
              <h4 className="font-semibold text-gray-900 mb-1">Through Funeral Home</h4>
              <p className="text-gray-600 text-sm">
                Most funeral homes will order death certificates on your behalf as part of their
                services. This is often the easiest method.
              </p>
            </div>
            <div className="border-l-4 border-purple-600 pl-4">
              <h4 className="font-semibold text-gray-900 mb-1">Directly from Vital Records</h4>
              <p className="text-gray-600 text-sm">
                You can order directly from your state's vital records office or county clerk,
                either online or by mail.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleNext} size="lg">
            Calculate Copies Needed
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </SettlementLayout>
  );
}
